import { useCallback, useRef, useState } from 'react';
import { APIConfig } from '@/constants';
import { Misc } from '@/constants';
import commonService from '@/services/common.service';

interface UseMagicPromptOptions {
    onPromptUpdate?: (prompt: string) => void;
    onStreamStart?: () => void;
    onStreamEnd?: () => void;
    onError?: (error: string) => void;
}

interface UseMagicPromptReturn {
    enhancePrompt: (projectId: string, payload: any) => Promise<void>;
    isProcessing: boolean;
}

export const useMagicPrompt = (options: UseMagicPromptOptions = {}): UseMagicPromptReturn => {
    const { onPromptUpdate, onStreamStart, onStreamEnd, onError } = options;
    const isStreamingProcessed = useRef(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const enhancePrompt = useCallback(async (projectId: string, payload: any) => {
        if (!projectId || isStreamingProcessed.current) return;

        try {
            isStreamingProcessed.current = true;
            setIsProcessing(true);
            onStreamStart?.();

            const jwtToken = localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
            const AuthorizationHeaders = { Authorization: "Bearer " + jwtToken };
            const url = APIConfig.GENERATE_PROMPT_FROM_PM_CONVERSATION.URL(projectId);

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...AuthorizationHeaders,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to enhance prompt');
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('No response body');
            }

            const decoder = new TextDecoder();
            let buffer = "";
            let enhancedPrompt = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                let newlineIndex;
                while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                    const line = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 1);

                    if (!line || !line.startsWith("data:")) continue;

                    try {
                        const jsonStr = line.replace(/^data:\s*/, "");
                        const parsedData = JSON.parse(jsonStr);

                        if (parsedData.type === "optimized_prompt_delta" && parsedData.data?.delta) {
                            enhancedPrompt += parsedData.data.delta;
                            onPromptUpdate?.(enhancedPrompt.trim());
                        } else if (parsedData.type === "optimized_prompt_end" && parsedData.data?.content) {
                            onPromptUpdate?.(parsedData.data.content.trim());
                            onStreamEnd?.();
                        }
                    } catch (err) {
                        console.error("Error processing magic prompt stream line:", line, err);
                    }
                }
            }
        } catch (error: any) {
            console.error("Error enhancing prompt:", error);
            const errorMessage = error.message || "Error enhancing prompt";
            onError?.(errorMessage);
            commonService._alert.showToast(errorMessage, "error");
        }
        finally {
            isStreamingProcessed.current = false;
            setIsProcessing(false);
            onStreamEnd?.();
        }
    }, [onPromptUpdate, onStreamStart, onStreamEnd, onError]);

    return {
        enhancePrompt,
        isProcessing
    };
};
